import { defineElement, FC, useBindMethod, useListen, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { useStore } from '@atomify/kit';
import { ButtonRound } from '@atoms/button-round/button-round';
import { setButtonLabel } from '@atoms/button/utilities';
import { Container } from '@atoms/container/container';
import { BPDFormElement } from '@atoms/form-elements';
import { CheckboxElement } from '@atoms/form-elements/checkbox/checkbox';
import { SingleColumnRow } from '@atoms/row/row';
import { NETHERLANDS_ID } from '@constants/countries';
import {
    AreaSubscriptionForm,
    AreaSubscriptionNavigationBar,
} from '@features/area-subscription/components';
import { AREA_SUBSCRIPTION_MODAL_ID } from '@features/area-subscription/constants';
import {
    areaSubscriptionStore,
    setAreaSubscriptionAreaOnly,
    setAreaSubscriptionFormRequired,
    setAreaSubscriptionFormSubmitting,
    toggleProjectPhaseSelected,
    togglePropertyTypeSelected,
} from '@features/area-subscription/store';
import {
    AreaSubscriptionModalElement,
    AreaSubscriptionModalTranslation,
    AreaSubscriptionModalUpdateAction,
    AreaSubscriptionState,
    AreaSubscriptionTriggerSource,
    ExtendedAreaSubscriptionState,
} from '@features/area-subscription/types';
import {
    getHelperTextForSelection,
    getModalCloseConfirmationTranslations,
    updateModalElement,
} from '@features/area-subscription/utilities';
import {
    extractFormData,
    submitSelection,
} from '@features/area-subscription/utilities/form-helpers';
import {
    handleSelectionGTM,
    handleVirtualPageGTM,
} from '@features/area-subscription/utilities/handle-gtm-push';
import { closeModal, dismissCloseModal, openModal } from '@store/modals';
import {
    getSubscriptionAddress,
    getSubscriptionUser,
    isStoreUserDataComplete,
    isSubscriptionFormRequired,
} from '@store/subscription/store';
import { GTMPush } from '@utilities/gtm';
import {
    SelectedCountry,
    useAreaData,
    useButtonLoadingState,
    useCountryPostcodeValidator,
    usePostcodeChecker,
    useSelectedCountry,
} from '@utilities/hooks';

import { attachAreaSubscriptionModalElements } from './utilities/attach-elements';

const LIST_DISABLED_CLASS = 'area-subscription-list--disabled';
const HIDDEN_CLASS = 'u-hidden';

const MODAL_ACTION = 'data-modal-action';

const AreaSubscriptionModal: FC<AreaSubscriptionModalElement> = ({ element }) => {
    const [onModalClose] = useProp<() => void>('onModalClose');
    const [, setDirty, watchDirty] = useProp<boolean>('dirty', false);

    const [areaSubscriptionState, subscribe] =
        useStore<AreaSubscriptionState>(areaSubscriptionStore);

    let form: BPDFormElement | undefined;
    let modalData: AreaSubscriptionModalTranslation;

    const { areaOnlyToggleInput } = attachAreaSubscriptionModalElements();

    const postcodeChecker = usePostcodeChecker({
        element,
        hooks: {
            postcodeHook: '[js-hook-area-subscription-form-zipcode]',
            houseNumberHook: '[js-hook-area-subscription-form-housenumber]',
            houseNumberAdditionHook: '[js-hook-area-subscription-form-houseaddition]',
            streetNameHook: '[js-hook-area-subscription-form-streename]',
            placeNameHook: '[js-hook-area-subscription-form-city]',
        },
        onAddressUpdated: handleFormChange,
    });

    let selectedCountry: SelectedCountry | null = null;

    watchDirty((newDirty: boolean) => {
        element.modalCloseCta.setAttribute(MODAL_ACTION, newDirty ? 'closeConfirmation' : 'close');
        element.modal.hasConfirmationPopup = newDirty;
    });

    useBindMethod('openModal', async (source: AreaSubscriptionTriggerSource) => {
        openModal(AREA_SUBSCRIPTION_MODAL_ID);
        const { areaProject } = await useAreaData();

        GTMPush({
            event: 'newsletterSubscription',
            eventCategory: 'newsletter',
            eventAction: `start flow - ${source ? source : 'content'}`,
            eventLabel: areaProject?.name,
        });

        await handleVirtualPageGTM({
            step: element.bpdPages.selected,
            areaOnly: areaSubscriptionState.areaOnly ?? false,
        });
    });

    useBindMethod('updateModalInactive', async (state: ExtendedAreaSubscriptionState) => {
        if (!state.status || element.modal.active) return;

        switch (state.status) {
            case 'notSubscribedToArea':
            case 'userNotSubscribedToArea':
            case 'subscribedToAreaNotSubscribedToProjects':
            case 'subscribedToAreaSubscribedToSomeProjects': {
                modalData = await updateModalElement(element, state, {
                    step: 0,
                    areaOnly: false,
                });
                setAreaSubscriptionAreaOnly(false);
                break;
            }
            case 'notSubscribedToAreaNoProjectsAvailable': {
                await renderForm({ areaOnly: true });
                modalData = await updateModalElement(element, state, {
                    step: 1,
                    areaOnly: true,
                });
                await setNavigationForFormValid(isFormValid());
                setAreaSubscriptionAreaOnly(true);
                break;
            }
            case 'userNotSubscribedToAreaNoProjectsAvailable': {
                await renderForm({ areaOnly: true });
                const formValid = isFormValid();

                if (formValid) {
                    setAreaSubscriptionFormSubmitting(true);
                    await submitSelection({
                        step: 0,
                        formData: { userInfo: getSubscriptionUser()! },
                    });
                    modalData = await updateModalElement(element, state, {
                        step: 2,
                        areaOnly: true,
                    });
                } else {
                    modalData = await updateModalElement(element, state, {
                        step: 1,
                        areaOnly: true,
                    });
                    await setNavigationForFormValid(isFormValid());
                }
                setAreaSubscriptionAreaOnly(true);
                break;
            }
            case 'subscribedToAreaSubscribedToAllProjects': {
                modalData = await updateModalElement(element, state, {
                    step: 2,
                    areaOnly: false,
                });
                setAreaSubscriptionAreaOnly(false);
                break;
            }
        }

        const data = await getModalCloseConfirmationTranslations();
        element.modalCloseCta.setAttribute(MODAL_ACTION, 'close');
        element.modalCloseConfirmation.setData(data);
    });

    useBindMethod(
        'updateModalActive',
        async (
            state: ExtendedAreaSubscriptionState,
            options?: { modalActiveAction?: AreaSubscriptionModalUpdateAction },
        ) => {
            if (options?.modalActiveAction === 'login') {
                handleClose({ force: true });
                return;
            }

            if (options?.modalActiveAction !== 'submit') return;
            setAreaSubscriptionFormSubmitting(false);

            const { areaOnly = false } = areaSubscriptionState;
            switch (element.bpdPages.selected) {
                case 0:
                case 1: {
                    modalData = await updateModalElement(element, state, {
                        step: 2,
                        areaOnly,
                    });
                    break;
                }
                case 2: {
                    modalData = await updateModalElement(element, state, {
                        step: 3,
                        areaOnly,
                    });
                    break;
                }
            }
        },
    );

    const handleNextStepClick = async () => {
        const { areaOnly = false, formRequired, selectedPropertyTypes } = areaSubscriptionState;

        switch (element.bpdPages.selected) {
            case 0: {
                await handleSelectionGTM({ step: 0, areaOnly });

                if (formRequired) {
                    await renderForm({ areaOnly });
                    modalData = await updateModalElement(element, areaSubscriptionState, {
                        step: 1,
                        areaOnly,
                    });
                    await setNavigationForFormValid(isFormValid());
                } else {
                    setAreaSubscriptionFormSubmitting(true);
                    await submitSelection({
                        step: 0,
                        formData: extractFormData(form, { areaOnly }),
                    });
                }

                break;
            }
            case 1: {
                setAreaSubscriptionFormSubmitting(true);
                await submitSelection({
                    step: 1,
                    formData: extractFormData(form, { areaOnly }),
                });
                break;
            }
            case 2: {
                if (selectedPropertyTypes.length) {
                    await handleSelectionGTM({ step: 2, areaOnly });
                    setAreaSubscriptionFormSubmitting(true);
                    await submitSelection({
                        step: 2,
                        formData: extractFormData(form, { areaOnly }),
                    });
                } else {
                    handleForceClose();
                }
                break;
            }
            case 3: {
                handleForceClose();
                break;
            }
        }
    };

    useBindMethod('nextStepClick', handleNextStepClick);

    subscribe(
        async ({ selectedProjectPhases }) => {
            if (element.bpdPages.selected !== 0) return;

            if (!selectedProjectPhases.length) {
                element.navigationCta.setAttribute('disabled', 'disabled');
                element.navigationCheckmark.classList.add(HIDDEN_CLASS);
                element.step1AvailableList.selectAllToggleInput &&
                    (element.step1AvailableList.selectAllToggleInput.checked = false);
            } else {
                element.navigationCta.removeAttribute('disabled');
                element.navigationCheckmark.classList.remove(HIDDEN_CLASS);
                setDirty(true);
            }

            const { navigationHelperTextProjectsSelect } = await await getHelperTextForSelection({
                step: 0,
                selected: selectedProjectPhases.length,
            });
            element.navigationHelperText.innerHTML = navigationHelperTextProjectsSelect!;
            modalData.navigationHelperTextProjectsSelect = navigationHelperTextProjectsSelect!;
        },
        ['selectedProjectPhases'],
    );

    subscribe(
        async ({ selectedPropertyTypes }) => {
            if (selectedPropertyTypes.length) {
                setDirty(true);
                modalData &&
                    setButtonLabel(
                        element.navigationCta,
                        modalData.navigationCtaCompleteRegistration!,
                    );
            } else {
                modalData && setButtonLabel(element.navigationCta, modalData.navigationCtaClose!);
            }
        },
        ['selectedPropertyTypes'],
    );

    subscribe(
        async ({ submitting }) => {
            if (submitting) {
                setNavigationCtaLoading(true);
            } else {
                setDirty(false);
                setNavigationCtaLoading(false);
            }
        },
        ['submitting'],
    );

    subscribe(
        async ({ areaOnly, selectedProjectPhases }) => {
            if (areaOnly) {
                element.step1AvailableList.classList.add(LIST_DISABLED_CLASS);

                element.navigationCta.removeAttribute('disabled');
                element.navigationHelperText.innerHTML = modalData.navigationHelperTextAreaOnly!;
                element.navigationCheckmark.classList.remove(HIDDEN_CLASS);
            } else {
                element.step1AvailableList.classList.remove(LIST_DISABLED_CLASS);

                element.navigationHelperText.innerHTML =
                    modalData.navigationHelperTextProjectsSelect!;
                if (selectedProjectPhases.length === 0) {
                    element.navigationCta.setAttribute('disabled', 'disabled');
                    element.navigationCheckmark.classList.add(HIDDEN_CLASS);
                }
                areaOnlyToggleInput.current && (areaOnlyToggleInput.current.checked = false);
            }

            const formRequiredOptions = isSubscriptionFormRequired();
            const formRequired = areaOnly ? formRequiredOptions.area : formRequiredOptions.project;
            setAreaSubscriptionFormRequired(formRequired);

            if (formRequired) {
                setButtonLabel(element.navigationCta, modalData.navigationCtaNextStep!);
            } else {
                setButtonLabel(element.navigationCta, modalData.navigationCtaCompleteRegistration!);
            }
        },
        ['areaOnly'],
    );

    useListen(areaOnlyToggleInput, 'change', async () => {
        setDirty(true);
        setAreaSubscriptionAreaOnly(areaOnlyToggleInput.current?.checked ?? false);
    });

    function handleFormChange() {
        const countryInput = element.querySelector<HTMLSelectElement>(
            '[js-hook-select-country-input]',
        );
        const newCountrySelection = countryInput && useSelectedCountry(countryInput);

        if (newCountrySelection?.id && selectedCountry?.id !== newCountrySelection?.id) {
            selectedCountry = newCountrySelection;
            useCountryPostcodeValidator(selectedCountry);

            if (selectedCountry.id === NETHERLANDS_ID) {
                postcodeChecker?.enable();
            } else {
                postcodeChecker?.disable();
            }
        }

        setNavigationForFormValid(isFormValid());
    }

    async function renderForm({ areaOnly }: { areaOnly: boolean }) {
        const userInfo = getSubscriptionUser();
        const addressInfo = getSubscriptionAddress();

        const mergeUserInfoAndAddressInfo = { ...userInfo, ...addressInfo };
        form = (
            <AreaSubscriptionForm
                areaOnly={areaOnly}
                preFilledInputs={mergeUserInfoAndAddressInfo}
                formReversed={isStoreUserDataComplete(userInfo)}
            />
        ) as BPDFormElement;

        element.areaSubscriptionForm.innerHTML = '';
        element.areaSubscriptionForm.append(form);

        await form.componentOnReady();

        !areaOnly && postcodeChecker?.initialize();
        form.formInner.addEventListener('change', handleFormChange);
    }

    const isFormValid = () => {
        if (!form) return false;
        const { valid } = form.validate(false);
        return postcodeChecker?.isEnabled ? valid && postcodeChecker?.checkValidity() : valid;
    };

    const setNavigationForFormValid = async (valid: boolean) => {
        if (!valid) {
            element.navigationHelperText.innerHTML = modalData.navigationHelperTextFormInvalid!;
            element.navigationCta.setAttribute('disabled', 'disabled');
            element.navigationCheckmark.classList.add(HIDDEN_CLASS);
        } else {
            element.navigationHelperText.innerHTML = modalData.navigationHelperTextFormValid!;
            element.navigationCta.removeAttribute('disabled');
            element.navigationCheckmark.classList.remove(HIDDEN_CLASS);
        }
    };

    const setNavigationCtaLoading = useButtonLoadingState(
        '[js-hook-area-subscription-navigation-cta]',
    );

    const handleClose = (options?: { force: boolean }) => {
        if (options?.force || element.modalCloseCta.getAttribute(MODAL_ACTION) === 'close') {
            closeModal(AREA_SUBSCRIPTION_MODAL_ID);
            setDirty(false);
            onModalClose();
        }
    };

    const handleForceClose = () => handleClose({ force: true });

    const handleDismissClose = () => dismissCloseModal(AREA_SUBSCRIPTION_MODAL_ID);

    return (
        <bpd-modal id={AREA_SUBSCRIPTION_MODAL_ID} onModalClose={onModalClose}>
            <div class="area-subscription-modal__container">
                <ButtonRound
                    ariaLabel="Modal button"
                    svg="icons-close"
                    variant="light"
                    size="small"
                    classes="area-subscription-modal__close-button"
                    hook="modal-trigger"
                    attributes={{ ['aria-controls']: AREA_SUBSCRIPTION_MODAL_ID }}
                    onClick={handleClose}
                />
                <modal-close-confirmation-popup
                    class="area-subscription-modal__close-confirmation"
                    modal-id={AREA_SUBSCRIPTION_MODAL_ID}
                    onConfirm={handleForceClose}
                    onDismiss={handleDismissClose}
                />
                <bpd-pages
                    selector="[area-subscription-modal-page]"
                    class="area-subscription-modal__pages-wrapper"
                    animated>
                    <Container
                        attr={{ ['area-subscription-modal-page']: true }}
                        extraClass="area-subscription-modal__page">
                        <SingleColumnRow
                            cols={[12, , 11]}
                            extraClass="u-margin-top-6 u-margin-bottom-2">
                            <p class="u-h5 u-h3--md" js-hook-area-subscription-modal-step-1-title />
                        </SingleColumnRow>
                        <SingleColumnRow
                            cols={[12, , 10]}
                            extraClass="u-margin-top-2 u-margin-bottom-2">
                            <p
                                class="s-rich-text"
                                js-hook-area-subscription-modal-step-1-description
                            />
                        </SingleColumnRow>
                        <SingleColumnRow
                            cols={[12, , 10]}
                            extraClass="u-margin-top-4 u-margin-bottom-2">
                            <CheckboxElement
                                name="subscribe-area-only"
                                id="area-subscription-subscribe-area-only-option"
                                hook="area-subscription-subscribe-area-only-toggle-input"
                                bpdInputHook="area-subscription-subscribe-area-only-toggle"
                                labelHook="area-subscription-subscribe-area-only-toggle-label"
                                classes="u-hidden"
                                label=""
                                hideError
                            />
                        </SingleColumnRow>
                        <SingleColumnRow
                            cols={[12, , 11]}
                            extraClass="u-margin-top-2 u-margin-bottom-2">
                            <area-subscription-list
                                item-type="projectPhase"
                                class="c-area-subscription-list"
                                js-hook-area-subscription-modal-step-1-subscribed-list
                            />
                        </SingleColumnRow>
                        <SingleColumnRow cols={[12, , 11]} extraClass="u-margin-top-2">
                            <area-subscription-list
                                item-type="projectPhase"
                                class="c-area-subscription-list"
                                js-hook-area-subscription-modal-step-1-available-list
                                onItemClick={toggleProjectPhaseSelected}
                                selectable
                                has-select-all
                            />
                        </SingleColumnRow>
                    </Container>

                    <Container
                        attr={{ ['area-subscription-modal-page']: true }}
                        extraClass="area-subscription-modal__page">
                        <div js-hook-area-subscription-modal-step-2-form />
                    </Container>

                    <Container
                        attr={{ ['area-subscription-modal-page']: true }}
                        extraClass="area-subscription-modal__page">
                        <SingleColumnRow
                            cols={[12, , 11]}
                            extraClass="u-margin-top-6 u-margin-bottom-2">
                            <p class="u-h5 u-h3--md" js-hook-area-subscription-modal-step-3-title />
                        </SingleColumnRow>
                        <SingleColumnRow cols={[12, , 10]} extraClass="u-margin-top-1">
                            <div js-hook-area-subscription-modal-step-3-confirmation />
                        </SingleColumnRow>
                        <SingleColumnRow cols={[12, , 10]} extraClass="u-margin-top-1">
                            <div js-hook-area-subscription-modal-step-3-advantages />
                        </SingleColumnRow>
                        <SingleColumnRow cols={[12, , 11]} extraClass="u-margin-top-1">
                            <p
                                class="u-body-normal-bold u-h6--md"
                                js-hook-area-subscription-modal-step-3-list-intro-title
                            />
                            <p
                                class="u-margin-top-2 u-margin-bottom-2"
                                js-hook-area-subscription-modal-step-3-list-intro-content
                            />
                            <area-subscription-list
                                item-type="propertyType"
                                class="c-area-subscription-list"
                                js-hook-area-subscription-modal-step-3-available-list
                                onItemClick={togglePropertyTypeSelected}
                                selectable
                            />
                        </SingleColumnRow>
                    </Container>

                    <Container
                        attr={{ ['area-subscription-modal-page']: true }}
                        extraClass="area-subscription-modal__page">
                        <SingleColumnRow
                            cols={[12, , 11]}
                            extraClass="u-margin-top-6 u-margin-bottom-2">
                            <p class="u-h5 u-h3--md" js-hook-area-subscription-modal-step-4-title />
                        </SingleColumnRow>
                        <SingleColumnRow cols={[12, , 10]} extraClass="u-margin-top-1">
                            <div js-hook-area-subscription-modal-step-4-confirmation />
                        </SingleColumnRow>
                        <SingleColumnRow cols={[12, , 10]} extraClass="u-margin-top-1">
                            <div js-hook-area-subscription-modal-step-4-advantages />
                        </SingleColumnRow>
                    </Container>
                </bpd-pages>

                <AreaSubscriptionNavigationBar
                    classes="area-subscription-modal__navigation"
                    ctaAction={handleNextStepClick}
                />
            </div>
        </bpd-modal>
    );
};

AreaSubscriptionModal.props = {
    onModalClose: {
        type: Function,
        required: true,
    },
    dirty: {
        type: Boolean,
    },
};

defineElement('area-subscription-modal', AreaSubscriptionModal);
